import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import Container from '@cloudscape-design/components/container';
import Box from '@cloudscape-design/components/box';
import Link from '@cloudscape-design/components/link';
import Cards from '@cloudscape-design/components/cards';
import Pagination from '@cloudscape-design/components/pagination';
import Tabs from '@cloudscape-design/components/tabs';
import Badge from '@cloudscape-design/components/badge';
import Alert from '@cloudscape-design/components/alert';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Modal from '@cloudscape-design/components/modal';
import Input from '@cloudscape-design/components/input';
import FormField from '@cloudscape-design/components/form-field';
import Icon from '@cloudscape-design/components/icon';
import { programsApi } from '../api/programs';
import { cohortsApi, Cohort } from '../api/cohorts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAddProgramAdmin, useRemoveProgramAdmin, useTransferProgramOwnership } from '../hooks/queries/usePrograms';
import { queryKeys } from '../lib/queryClient';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorAlert } from '../components/ErrorAlert';
import { useBreadcrumbs } from '../contexts';
import { buildBreadcrumbs } from '../components/BreadcrumbBuilder';
import { useAuthStore } from '../stores/authStore';
import { useIsProgramAdminOrOwner } from '../hooks/usePermissions';
import { CategoryBadge } from '../components/CategoryBadge';
import { UserRoleBadge } from '../components/UserRoleBadge';
import { UserSearchSelect } from '../components/UserSearchSelect';
import { UserSearchResult } from '../api/users';
import { getUserCohortRelationship } from '../utils/userRelationships';
import { PersonChip } from '../components/PersonChip';
import { ProgramMetricsTab } from './program-tabs/ProgramMetricsTab';
import { SlackIcon } from '../components/SlackIcon';
import { GlobeIcon } from '../components/GlobeIcon';
import { HelpPopover } from '../components/HelpPopover';
import { badgePhotoUrl, phoneToolUrl } from '../lib/externalUrls';

// Format date for badges (e.g., "3/15" or "12/31")
const formatBadgeDate = (dateStr: string): string => {
  const [, month, day] = dateStr.split('-').map(Number);
  return `${month}/${day}`;
};

// Format ended date with 2-digit year (e.g., "3/15/24")
const formatEndedDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return `${month}/${day}/${String(year).slice(2)}`;
};

function ProgramDetailsPage() {
  const { programId } = useParams<{ programId: string }>();
  const { setBreadcrumbs } = useBreadcrumbs();
  const currentUser = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [newAdmin, setNewAdmin] = useState<UserSearchResult | null>(null);
  const [addAdminError, setAddAdminError] = useState<string | null>(null);
  const { mutateAsync: addAdmin, isPending: addAdminLoading } = useAddProgramAdmin();
  const { mutateAsync: removeAdmin, isPending: removeAdminLoading } = useRemoveProgramAdmin();
  const [showRemoveAdminModal, setShowRemoveAdminModal] = useState(false);
  const [adminToRemove, setAdminToRemove] = useState<{ userId: string; name: string } | null>(null);
  const [removeAdminError, setRemoveAdminError] = useState<string | null>(null);
  const { mutateAsync: transferOwnership, isPending: transferOwnerLoading } = useTransferProgramOwnership();
  const [showReassignOwnerModal, setShowReassignOwnerModal] = useState(false);
  const [reassignOwner, setReassignOwner] = useState<UserSearchResult | null>(null);
  const [reassignOwnerError, setReassignOwnerError] = useState<string | null>(null);
  const [reassignConfirmText, setReassignConfirmText] = useState('');
  const isAppAdmin = !!(currentUser?.isAdmin);

  const openReassignOwnerModal = () => {
    setReassignOwner(null);
    setReassignConfirmText('');
    setReassignOwnerError(null);
    setShowReassignOwnerModal(true);
  };

  const openAddAdminModal = () => {
    setNewAdmin(null);
    setAddAdminError(null);
    setShowAddAdminModal(true);
  };

  const openRemoveAdminModal = (admin: { userId: string; name: string }) => {
    setAdminToRemove(admin);
    setRemoveAdminError(null);
    setShowRemoveAdminModal(true);
  };

  const [activeTabId, setActiveTabId] = useState('cohorts');
  const [cohortsPageIndex, setCohortsPageIndex] = useState(1);
  const COHORTS_PER_PAGE = 6;

  // Fetch program details (includes admins)
  const {
    data: program,
    isLoading: programLoading,
    error: programError,
  } = useQuery({ queryKey: queryKeys.programs.detail(programId!), queryFn: () => programsApi.get(programId!), enabled: !!programId });

  // Fetch cohorts for this program
  const {
    data: cohorts,
    isLoading: cohortsLoading,
    error: cohortsError,
  } = useQuery({ queryKey: queryKeys.cohorts.list(programId), queryFn: () => cohortsApi.list(programId!), enabled: !!programId });

  // isAdmin covers both program admins and owners
  const isAdmin = useIsProgramAdminOrOwner(programId);
  const isProgramOwner = !!(currentUser?.userId && program?.ownerId && currentUser.userId === program.ownerId);

  // Check if current user is a mentor in any cohort of this program
  const mentorFor = useAuthStore((state) => state.mentorFor);
  const isMentorFor = useAuthStore((state) => state.isMentorFor);
  const isParticipantIn = useAuthStore((state) => state.isParticipantIn);
  const isMentorInProgram = cohorts?.some(cohort => 
    mentorFor.some(m => m.cohortId === cohort.cohortId)
  ) || false;

  // Eligibility comes from the backend (isEligible computed server-side using supervisor chain)
  const userIsEligible = program?.visibility !== 'org-restricted' || program?.isEligible === true;

  // Set breadcrumbs when program data loads
  useEffect(() => {
    if (program) {
      setBreadcrumbs(buildBreadcrumbs({
        programId: program.programId,
        programName: program.programName,
      }));
    }
    return () => setBreadcrumbs([]);
  }, [program, setBreadcrumbs]);

  // Sort cohorts: eligible first, then ineligible
  const allCohorts = useMemo(() => {
    if (!cohorts) return [] as Cohort[];
    const eligible   = cohorts.filter(c => !c.visibility || c.visibility === 'public' || c.orgLeader === currentUser?.userId || currentUser?.supervisorChain.includes(c.orgLeader || ''));
    const ineligible = cohorts.filter(c => !eligible.includes(c));
    return [...eligible, ...ineligible];
  }, [cohorts, currentUser?.userId, currentUser?.supervisorChain]);

  if (!currentUser) return null;

  if (programLoading || cohortsLoading) {
    return <LoadingSpinner message="Loading program details..." />;
  }

  if (programError) {
    return <ErrorAlert error={programError} />;
  }

  if (!program) {
    return <ErrorAlert error="Program not found" />;
  }

  const cohortCardDefinition = {
    header: (item: Cohort) => {
      return (
        <SpaceBetween size="xs">
          <Link 
            fontSize="heading-m" 
            href="#"
            onFollow={(e) => {
              e.preventDefault();
              navigate(`/cohorts/${item.cohortId}`);
            }}
          >
            {item.cohortName}
          </Link>
        </SpaceBetween>
      );
    },
    sections: [
      {
        id: 'content',
        content: (item: Cohort) => {
          // Check if user is eligible for this cohort
          const isCohortEligible = !item.visibility || item.visibility === 'public' || 
            item.orgLeader === currentUser.userId || 
            currentUser.supervisorChain.includes(item.orgLeader || '');

          const userRelationship = getUserCohortRelationship(
            item.cohortId,
            isAdmin,
            isMentorFor(item.cohortId),
            isParticipantIn(item.cohortId),
            isProgramOwner,
          );

          return (
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100px', paddingTop: '8px' }}>
              <SpaceBetween size="xs">
                <div style={{ display: 'flex', gap: '24px' }}>
                  {item.registrationStartDate && item.registrationEndDate && (
                    <Box>
                      <Box variant="awsui-key-label">Registration</Box>
                      {new Date(item.registrationStartDate).toLocaleDateString()} - {new Date(item.registrationEndDate).toLocaleDateString()}
                    </Box>
                  )}
                  <Box>
                    <Box variant="awsui-key-label">Cohort Dates</Box>
                    {new Date(item.startDate).toLocaleDateString()} - {new Date(item.endDate).toLocaleDateString()}
                  </Box>
                </div>
                <div style={{ display: 'flex', gap: '24px' }}>
                  <Box>
                    <Box variant="awsui-key-label">Enrollment</Box>
                    {item.enrollmentCount || 0}{item.capacity ? ` / ${item.capacity}` : ''}
                  </Box>
                </div>
              </SpaceBetween>
              
              <div style={{ marginTop: 'auto', paddingTop: '16px' }}>
                <SpaceBetween size="xs">
                  {/* Chip badges + Enroll button row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '12px' }}>
                    {/* Left side: chip badges */}
                    <div style={{ flex: 1 }}>
                      <SpaceBetween direction="horizontal" size="xs">
                        {/* Timeline badges (green/grey) */}
                        {item.status === 'upcoming' && <Badge color="green">Starts {formatBadgeDate(item.startDate)}</Badge>}
                        {item.status === 'active' && <Badge color="green">Ends {formatBadgeDate(item.endDate)}</Badge>}
                        {item.status === 'completed' && <Badge color="grey">Ended {formatEndedDate(item.endDate)}</Badge>}
                        
                        {/* Registration badges (blue) */}
                        {item.status === 'registration-open' && item.registrationEndDate && (
                          <Badge color="blue">Registration ends {formatBadgeDate(item.registrationEndDate)}</Badge>
                        )}
                        {item.status === 'active' && item.registrationEndDate && new Date(item.registrationEndDate) > new Date() && (
                          <Badge color="blue">Registration ends {formatBadgeDate(item.registrationEndDate)}</Badge>
                        )}
                        {item.status === 'active' && (!item.registrationEndDate || new Date(item.registrationEndDate) <= new Date()) && (
                          <Badge>Registration closed</Badge>
                        )}
                        {/* TODO: Uncomment when isPublished flow is fully tested
                        {!item.isPublished && (
                          <Badge color="red">Unpublished</Badge>
                        )}
                        */}
                      </SpaceBetween>
                    </div>
                    
                    {/* Right side: Enroll Now button */}
                    {item.canEnroll && !isAdmin && !isMentorInProgram && (
                      <Button onClick={() => navigate(`/cohorts/${item.cohortId}`)}>
                        Enroll Now
                      </Button>
                    )}
                  </div>

                  {/* Status indicators row — separated from chip badges, grouped together */}
                  {(item.visibility === 'org-restricted' ||
                    (userRelationship && (userRelationship.role === 'mentor' || userRelationship.role === 'participant'))) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingTop: '8px' }}>
                      {item.visibility === 'org-restricted' && item.orgLeader && (
                        <StatusIndicator type={isCohortEligible ? 'info' : 'warning'}>
                          Restricted to <strong>{item.orgLeaderName ?? `@${item.orgLeader}`}</strong>{item.orgLeaderName ? ` (@${item.orgLeader})` : ''}
                        </StatusIndicator>
                      )}
                      {userRelationship && (userRelationship.role === 'mentor' || userRelationship.role === 'participant') && (
                        <UserRoleBadge role={userRelationship.role} context="cohort" />
                      )}
                    </div>
                  )}
                </SpaceBetween>
              </div>
            </div>
          );
        },
      },
    ],
  };

  const cardsPerRow = [
    { cards: 1 },
    { minWidth: 500, cards: 2 },
    { minWidth: 1000, cards: 3 },
  ];

  const renderCohortCards = (items: Cohort[]) => (
    <Cards
      cardDefinition={cohortCardDefinition}
      cardsPerRow={cardsPerRow}
      items={items}
      loadingText="Loading cohorts"
      trackBy="cohortId"
      empty={
        <Box textAlign="center" color="inherit">
          <Box variant="p" color="inherit">No cohorts</Box>
        </Box>
      }
    />
  );

  return (
    <>
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          (isAppAdmin || isProgramOwner || isAdmin) ? (
            <SpaceBetween direction="horizontal" size="xs">
              {isAppAdmin && (
                <ButtonDropdown
                  items={[{ id: 'reassign-owner', text: 'Reassign Ownership' }]}
                  onItemClick={({ detail }) => {
                    if (detail.id === 'reassign-owner') openReassignOwnerModal();
                  }}
                >
                  Admin Actions
                </ButtonDropdown>
              )}
              {(isAdmin || isProgramOwner) && (
                <Button
                  iconName="add-plus"
                  onClick={() => navigate(`/programs/${programId}/cohorts/create`)}
                >
                  Add Cohort
                </Button>
              )}
              {isProgramOwner && (
                <Button
                  iconName="settings"
                  onClick={() => navigate(`/programs/${programId}/edit`)}
                >
                  Settings
                </Button>
              )}
            </SpaceBetween>
          ) : undefined
        }
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}>
          {program.programName}
          <CategoryBadge category={program.category} />
        </span>
      </Header>

      {!userIsEligible && program.visibility === 'org-restricted' && (
        <Alert type="warning">
          This program is restricted to <strong>{program.orgLeaderName ?? `@${program.orgLeader}`}</strong>
          {program.orgLeaderName ? ` (@${program.orgLeader})` : ''}'s organization. You can view the program details but cannot enroll in cohorts.
        </Alert>
      )}

      {/* TODO: Uncomment when isPublished flow is fully tested
      {!program.isPublished && (isAdmin || isProgramOwner) && (
        <Alert type="info">
          This program is not yet published. It is only visible to you and program admins.{' '}
          <Link onFollow={(e) => { e.preventDefault(); navigate(`/programs/${programId}/edit`); }} href="#">
            Go to Settings
          </Link>
        </Alert>
      )}
      */}

      {cohortsError && <ErrorAlert error={cohortsError} />}

      {/* Program details — always visible above tabs */}
      <Container header={<Header variant="h2">Program details</Header>}>
        <SpaceBetween size="l">
          <Box>{program.description}</Box>
          {(program.wikiUrl || program.slackChannel) ? (
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              {program.wikiUrl && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <GlobeIcon size={16} />
                  <Link href={program.wikiUrl} external>Wiki</Link>
                </div>
              )}
              {program.slackChannel && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <SlackIcon size={16} />
                  <Link href={program.slackChannel} external>Slack Channel</Link>
                </div>
              )}
            </div>
          ) : null}

          {/* Program Owner */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
              <Box variant="awsui-key-label">Program Owner</Box>
              <HelpPopover
                header="Program Owner"
                content={
                  <>
                    The program owner has full control over the program. In addition to all program admin permissions, the owner can:
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '16px' }}>
                      <li>Edit and delete the program</li>
                      <li>Add and remove program admins</li>
                      <li>Transfer ownership to another manager</li>
                    </ul>
                  </>
                }
              />
            </div>
            {program.ownerId ? (
              <PersonChip login={program.ownerId} name={program.ownerName ?? program.ownerId} />
            ) : (
              <Box color="text-body-secondary">No owner assigned</Box>
            )}
          </div>

          {/* Program Admins */}
          {(isProgramOwner || (program.admins && program.admins.length > 0)) && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                <Box variant="awsui-key-label">Program Admins</Box>
                <HelpPopover
                  header="Program Admins"
                  content={
                    <>
                      Program admins help the owner manage the day-to-day operations of the program. Admins can:
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '16px' }}>
                        <li>Create and manage cohorts</li>
                        <li>Enroll and unenroll participants</li>
                        <li>Add, approve, and remove mentors</li>
                        <li>Create and manage activities</li>
                        <li>Review graduation requests</li>
                      </ul>
                    </>
                  }
                />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {[...(program.admins ?? [])].sort((a, b) => a.name.localeCompare(b.name)).map((admin) => (
                  <PersonChip
                    key={admin.userId}
                    login={admin.userId}
                    name={admin.name}
                    removeIcon="remove"
                    onRemove={isProgramOwner ? () => openRemoveAdminModal(admin) : undefined}
                  />
                ))}
                {isProgramOwner && (
                  <div
                    key="add-admin-placeholder"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '8px',
                      padding: '6px 12px 6px 6px', background: '#f2f3f3',
                      borderRadius: '8px', minWidth: '230px',
                    }}
                  >
                    <div style={{
                      width: 40, height: 40, borderRadius: 5, flexShrink: 0,
                      background: '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon name="user-profile" size="medium" />
                    </div>
                    <Button variant="inline-link" iconName="add-plus" onClick={openAddAdminModal}>
                      Add Admin
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </SpaceBetween>
      </Container>

      <Tabs
        activeTabId={activeTabId}
        onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
        tabs={[
          {
            id: 'cohorts',
            label: `Cohorts (${allCohorts.length})`,
            content: (() => {
              const pagesCount = Math.ceil(allCohorts.length / COHORTS_PER_PAGE);
              const pagedCohorts = allCohorts.slice(
                (cohortsPageIndex - 1) * COHORTS_PER_PAGE,
                cohortsPageIndex * COHORTS_PER_PAGE,
              );
              return (
                <SpaceBetween size="m">
                  {renderCohortCards(pagedCohorts)}
                  {pagesCount > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Pagination
                        currentPageIndex={cohortsPageIndex}
                        pagesCount={pagesCount}
                        onChange={({ detail }) => setCohortsPageIndex(detail.currentPageIndex)}
                      />
                    </div>
                  )}
                </SpaceBetween>
              );
            })(),
          },
          // Metrics tab is only available to program owners and admins
          ...(isAdmin ? [{
            id: 'metrics',
            label: 'Metrics',
            content: <ProgramMetricsTab cohorts={allCohorts} />,
          }] : []),
        ]}
      />
    </SpaceBetween>

      {/* Add Admin Modal */}
      {isProgramOwner && showAddAdminModal && (
        <Modal
          visible={showAddAdminModal}
          onDismiss={() => setShowAddAdminModal(false)}
          header="Add Program Admin"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setShowAddAdminModal(false)}>Cancel</Button>
                <Button
                  variant="primary"
                  disabled={!newAdmin}
                  loading={addAdminLoading}
                  onClick={async () => {
                    if (!newAdmin) return;
                    try {
                      setAddAdminError(null);
                      await addAdmin({ programId: programId!, userId: newAdmin.login, alias: newAdmin.login, name: newAdmin.name, email: newAdmin.email });
                      setShowAddAdminModal(false);
                      setNewAdmin(null);
                      queryClient.invalidateQueries({ queryKey: queryKeys.programs.detail(programId!) });
                    } catch (err: any) {
                      if (err.message?.includes('already a program admin') || err.response?.statusCode === 409) {
                        setAddAdminError(`${newAdmin.name} is already a program admin`);
                      } else {
                        setAddAdminError(err.message || 'Failed to add admin');
                      }
                    }
                  }}
                >
                  Add Admin
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            {addAdminError && (
              <Alert type="error" dismissible onDismiss={() => setAddAdminError(null)}>{addAdminError}</Alert>
            )}
            <FormField label="Search for user" description="Search by name or alias to add as a program admin">
              <UserSearchSelect
                key={newAdmin?.login ?? 'empty'}
                onSelect={(user) => setNewAdmin(user)}
                clearOnSelect={false}
                placeholder="Search by name or alias..."
              />
            </FormField>
            {newAdmin && (
              <SpaceBetween size="xs">
                <Box fontWeight="bold">Selected:</Box>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', background: '#f2f3f3', borderRadius: '8px' }}>
                  <img src={badgePhotoUrl(newAdmin.login)} alt={newAdmin.name} style={{ width: 40, height: 40, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Box fontWeight="bold">{newAdmin.name}</Box>
                    <Box color="text-body-secondary" fontSize="body-s"><Link href={phoneToolUrl(newAdmin.login)} external fontSize="body-s">@{newAdmin.login}</Link> — {newAdmin.title}</Box>
                  </div>
                  <Button variant="icon" iconName="close" ariaLabel="Clear selection" onClick={() => setNewAdmin(null)} />
                </div>
              </SpaceBetween>
            )}
          </SpaceBetween>
        </Modal>
      )}

      {/* Remove Admin Confirmation Modal */}
      <Modal
        visible={showRemoveAdminModal}
        onDismiss={() => setShowRemoveAdminModal(false)}
        header="Remove Program Admin"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowRemoveAdminModal(false)}>Cancel</Button>
              <Button variant="primary" loading={removeAdminLoading} onClick={async () => {
                if (!adminToRemove) return;
                try {
                  setRemoveAdminError(null);
                  await removeAdmin({ programId: programId!, userId: adminToRemove.userId });
                  queryClient.invalidateQueries({ queryKey: queryKeys.programs.detail(programId!) });
                  setShowRemoveAdminModal(false);
                  setAdminToRemove(null);
                } catch (err: any) {
                  setRemoveAdminError(err.message || 'Failed to remove admin');
                }
              }}>Remove</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {removeAdminError && (
            <Alert type="error" dismissible onDismiss={() => setRemoveAdminError(null)}>{removeAdminError}</Alert>
          )}
          <Box>
            Are you sure you want to remove <strong>{adminToRemove?.name}</strong> as a program admin?
            {adminToRemove?.userId === currentUser.userId && ' You will lose admin access to this program.'}
          </Box>
        </SpaceBetween>
      </Modal>

      {/* Reassign Ownership Modal — App Admin emergency override */}
      <Modal
        visible={showReassignOwnerModal}
        onDismiss={() => setShowReassignOwnerModal(false)}
        header="Reassign Program Ownership"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowReassignOwnerModal(false)}>Cancel</Button>
              <Button
                variant="primary"
                disabled={!reassignOwner || reassignConfirmText !== 'confirm'}
                loading={transferOwnerLoading}
                onClick={async () => {
                  if (!reassignOwner) return;
                  try {
                    setReassignOwnerError(null);
                    await transferOwnership({ programId: programId!, newOwnerId: reassignOwner.login, newOwnerName: reassignOwner.name, retainAsAdmin: false });
                    setShowReassignOwnerModal(false);
                  } catch (err: any) {
                    setReassignOwnerError(err.message || 'Failed to reassign ownership');
                  }
                }}
              >
                Reassign Ownership
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {reassignOwnerError && (
            <Alert type="error" dismissible onDismiss={() => setReassignOwnerError(null)}>{reassignOwnerError}</Alert>
          )}
          <Alert type="error">
            <strong>Emergency use only.</strong> This will immediately remove <strong>{program?.ownerName ?? program?.ownerId}</strong> as program owner and replace them with the person you select. The current owner will lose all access to this program. Only use this when the program owner is unavailable and ownership must be transferred.
          </Alert>
          <FormField label="New owner" description="Search for a manager to assign as the new program owner.">
            <UserSearchSelect
              key={reassignOwner?.login ?? 'empty'}
              onSelect={(user) => setReassignOwner(user)}
              clearOnSelect={false}
              managersOnly={true}
              placeholder="Search by name or alias..."
            />
          </FormField>
          {reassignOwner && (
            <SpaceBetween size="xs">
              <Box fontWeight="bold">Selected:</Box>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', background: '#f2f3f3', borderRadius: '8px' }}>
                <img src={badgePhotoUrl(reassignOwner.login)} alt={reassignOwner.name} style={{ width: 40, height: 40, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Box fontWeight="bold">{reassignOwner.name}</Box>
                  <Box color="text-body-secondary" fontSize="body-s"><Link href={phoneToolUrl(reassignOwner.login)} external fontSize="body-s">@{reassignOwner.login}</Link> — {reassignOwner.title}</Box>
                </div>
                <Button variant="icon" iconName="close" ariaLabel="Clear selection" onClick={() => setReassignOwner(null)} />
              </div>
            </SpaceBetween>
          )}
          <FormField label={<>Type <strong>confirm</strong> to proceed</>}>
            <Input
              value={reassignConfirmText}
              onChange={({ detail }) => setReassignConfirmText(detail.value)}
              placeholder="confirm"
              autoComplete="off"
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </>
  );
}

export default ProgramDetailsPage;